import {Button} from '@wandb/weave/components/Button';
import * as DropdownMenu from '@wandb/weave/components/DropdownMenu';
import {Icon, IconName} from '@wandb/weave/components/Icon';
import classNames from 'classnames';
import React, {useState} from 'react';

import {Analytics} from '../../services/analytics';

export type MenuItem = {
  id: string;
  name: string;
  icon?: IconName;
  onSelect?: () => void;
  /** A `MenuItem` with `groups` renders a submenu */
  groups?: MenuGroup[];
};

export type MenuGroup = {
  items: MenuItem[];
  /** A `MenuGroup` with `value` renders a radio group */
  value?: string;
  onValueChange?: (newValue: string) => void;
};

export type WBTableCellColumnHeaderMenuProps = {
  isInReport?: boolean;
  groups: MenuGroup[];
};

const Item = (props: MenuItem) => {
  const {onSelect, icon, name, groups} = props;
  const isSubmenu = !!groups?.length;
  if (isSubmenu) {
    return <Submenu {...props} />;
  }
  return (
    <DropdownMenu.Item onSelect={onSelect}>
      {icon && <Icon name={icon} />}
      {name}
    </DropdownMenu.Item>
  );
};

const RadioItem = ({id, icon, name}: MenuItem) => {
  return (
    <DropdownMenu.RadioItem value={id}>
      {icon && <Icon name={icon} />}
      {name}
      <DropdownMenu.ItemIndicatorRadio />
    </DropdownMenu.RadioItem>
  );
};

const Submenu = ({icon, name, groups}: MenuItem) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <DropdownMenu.Sub open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenu.SubTrigger>
        {icon && <Icon name={icon} />}
        {name}
        <Icon name="chevron-next" className="ml-auto text-moon-450" />
      </DropdownMenu.SubTrigger>
      <DropdownMenu.Portal>
        <DropdownMenu.SubContent className="z-[200] w-[8rem]">
          {groups?.map((group, idx) => (
            <Group key={idx} idx={idx} {...group} />
          ))}
        </DropdownMenu.SubContent>
      </DropdownMenu.Portal>
    </DropdownMenu.Sub>
  );
};

const Group = ({
  items,
  value,
  onValueChange,
  idx,
}: MenuGroup & {idx: number}) => {
  const isRadioGroup = value !== undefined;
  return (
    <>
      {idx > 0 && <DropdownMenu.Separator />}
      {isRadioGroup ? (
        <DropdownMenu.RadioGroup value={value} onValueChange={onValueChange}>
          {items.map(item => (
            <RadioItem key={item.id} {...item} />
          ))}
        </DropdownMenu.RadioGroup>
      ) : (
        items.map(item => <Item key={item.id} {...item} />)
      )}
    </>
  );
};

export const WBTableCellColumnHeaderMenu = ({
  isInReport,
  groups,
}: WBTableCellColumnHeaderMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <DropdownMenu.Root open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenu.Trigger>
        <Button
          icon="overflow-horizontal"
          variant="ghost"
          size="small"
          active={isOpen}
          className={classNames('column-actions-trigger mr-8', {
            '!opacity-100': isOpen,
          })}
          onClick={() => {
            const actionLocation = isInReport ? 'report' : 'runs table';
            Analytics.track('Column header dropdown clicked', {
              location: actionLocation,
            });
          }}
        />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content align="start" className="z-[200]">
          {groups.map((group, idx) => (
            <Group key={idx} idx={idx} {...group} />
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};
